import React, { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useBhimaBulkData } from '../BhimaBulkDataContext';
import { getJSONObject } from 'utils/helper';

export default function BhimaDonutTotalChart({ title, apiParams }) {
    const { loading: bulkLoading, dataByKey } = useBhimaBulkData();
    const [rows, setRows] = useState([]);
    const [plot, setPlot] = useState({});

    const response = useMemo(() => {
        if (apiParams?.chartid == null || apiParams?.dashboard_id == null) return null;
        return dataByKey[`${apiParams.dashboard_id}:${apiParams.chartid}`] ?? null;
    }, [dataByKey, apiParams?.dashboard_id, apiParams?.chartid]);

    useEffect(() => {
        if (!response) return;
        const result = response?.results?.[0];
        if (!result) return;
        if (result.error) {
            setRows([]);
            setPlot({});
            return;
        }
        const hc = getJSONObject(result.highchart_code);
        const dataRows = getJSONObject(result.df_data) || [];
        setRows(Array.isArray(dataRows) ? dataRows : []);
        setPlot(typeof hc === 'object' ? hc : {});
    }, [response]);

    const result = response?.results?.[0];
    const hasError = Boolean(result?.error);

    const xKey = useMemo(() => String(plot?.x_axis || 'Scheme'), [plot]);
    const yKeys = useMemo(() => Array.isArray(plot?.y_axis) ? plot.y_axis : [plot?.y_axis].filter(Boolean), [plot]);
    const valueKey = useMemo(() => (yKeys && yKeys[0]) || 'commitment_value', [yKeys]);

    const total = useMemo(() => {
        if (!Array.isArray(rows)) return 0;
        const byOverallTotal = rows.find(r => typeof r?.overall_total === 'number')?.overall_total;
        if (typeof byOverallTotal === 'number') return byOverallTotal;
        const fromTotalRow = rows.find(r => String(r?.[xKey]).toLowerCase() === 'total' && typeof r?.[valueKey] === 'number')?.[valueKey];
        if (typeof fromTotalRow === 'number') return fromTotalRow;
        return rows.reduce((sum, r) => sum + (typeof r?.[valueKey] === 'number' ? r[valueKey] : 0), 0);
    }, [rows, valueKey, xKey]);

    const formatCrore = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '';
        const crores = num / 10000000;
        const txt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(crores);
        return `₹${txt}Cr`;
    };

    const seriesData = useMemo(() => {
        if (!Array.isArray(rows)) return [];
        return rows
        .filter(r => String(r?.[xKey]).toLowerCase() !== 'total')
        .map(r => {
            const name = r?.[xKey];
            const y = typeof r?.[valueKey] === 'number' ? r[valueKey] : null;
            if (y === null) return null;
            return { 
                name: String(name ?? ''), 
                y,
                percentage_share: r?.percentage_share 
            };
        }).filter(Boolean);
    }, [rows, xKey, valueKey]);

    const options = useMemo(() => ({
        colors: [
            '#d4af37', // Metallic Gold
            '#c5a089', // Rose Gold
            '#a65e5e', // Deep Rose
            '#4a7c78', // Vintage Teal
            '#5d7a96', // Muted Blue
            '#8c6a3e', // Bronze
            '#5c442d', // Dark Wood
            '#9e857d'  // Muted Mauve
        ],
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 320
        },
        title: {
            text: '',
        },
        credits: { enabled: false },
        tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#9e857d',
            borderRadius: 2,
            style: { color: '#3e2b26', fontFamily: 'Playfair Display, serif' },
            formatter: function () {
                const pct = this.point.percentage_share;
                const pctStr = (typeof pct === 'number') ? ` (${pct.toFixed(2)}%)` : '';
                return `<b>${this.point.name}</b>: ${formatCrore(this.point.y)}${pctStr}`;
            }
        },
        legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemStyle: {
                color: '#3e2b26',
                fontFamily: 'Playfair Display, serif',
                fontSize: '12px',
                fontWeight: 'bold'
            },
            itemHoverStyle: {
                color: '#000000'
            },
            symbolRadius: 0
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                dataLabels: {
                    enabled: false
                },
                showInLegend: true
            }
        },
        series: [{
            name: valueKey,
            data: seriesData
        }]
    }), [seriesData, valueKey]);

    return (
        <div className="bhima-card" style={{ paddingBottom: '15px' }}>
            <div className="bhima-card-title" style={{ minHeight: 'auto', marginBottom: '5px', padding: '0 10px' }}>
                {title}
            </div>

            {hasError ? (
                <div className="bhima-no-data">No data available</div>
            ) : (
            <div style={{ position: 'relative', width: '100%', maxWidth: '600px' }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '43%',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Cinzel, serif',
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#f4e4bc',
                    background: 'radial-gradient(circle at 30% 30%, #5c442d, #3e2b26)',
                    borderRadius: '60px',
                    padding: '10px 18px',
                    border: '1px solid #8c6a3e',
                    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.2)'
                }}>
                    {formatCrore(total)}
                </div>
            </div>
            )}
        </div>
    );
}
